import {
  forwardRef,
  memo,
  ReactNode,
  useState,
  useEffect,
  FocusEvent,
  useMemo
} from 'react';

import { RiArrowDownSLine, RiErrorWarningLine } from 'react-icons/ri';

import {
  Box,
  BoxProps,
  Grid,
  Input,
  Loader,
  MantineSize,
  Select,
  useMantineColorScheme
} from '@mantine/core';

import { NumberInput } from '../../inputs';
import {
  GroupSelectOverrideClassNames,
  GroupInputOverrideClassNames
} from '../groupstyles';

const ONE_CRORE = 10000000;
const ONE_LAKH = 100000;

interface CoverSelectGroupProps extends BoxProps {
  /**
   * enable loading
   */
  loading?: boolean;

  label?: string;

  description?: string;

  value?: number;

  numberPlaceholder?: string;
  /**
   * Placeholder for select field
   */
  selectPlaceholder?: string;
  /**
   * Icon for number field
   */
  numberIcon?: ReactNode;
  /**
   * icon for select field
   */
  selectIcon?: ReactNode;
  useCurrencyFormatter?: boolean;
  /**
   * Enables decimal option in number field with
   * exact precision
   */
  decimalScale?: number;
  disabled?: boolean;
  size?: MantineSize;
  error?: string;
  defaultUnit?: 'lakhs' | 'crores';
  onChange?: (value: number) => void;
}

const CoverSelectGroup = memo(
  forwardRef<HTMLInputElement, CoverSelectGroupProps>(
    (
      {
        size = 'md',
        loading,
        label,
        description,
        value,
        numberPlaceholder,
        selectPlaceholder,
        numberIcon,
        selectIcon,
        disabled,
        decimalScale,
        error,
        defaultUnit,
        useCurrencyFormatter,
        onChange,
        ...rest
      },
      ref
    ) => {
      const scheme = useMantineColorScheme();
      const initVal = () => {
        const init = value
          ? value >= ONE_CRORE
            ? value / ONE_CRORE
            : value >= ONE_LAKH
            ? value / ONE_LAKH
            : value
          : undefined;

        return init;
      };
      const [inputFocussed, setInputFocussed] = useState<boolean>(false);
      const [selectFocussed, setSelectFocussed] = useState<boolean>(false);

      const [localValue, setLocalValue] = useState<number | undefined>(initVal());
      const [localUnit, setLocalUnit] = useState<string>(
        defaultUnit ? defaultUnit : value && value < ONE_CRORE ? 'lakhs' : 'crores'
      );

      useEffect(() => {
        if (defaultUnit) {
          setLocalUnit(defaultUnit);
        }
      }, [defaultUnit]);

      useEffect(() => {
        if (value) {
          const finalVal = value / (localUnit === 'lakhs' ? ONE_LAKH : ONE_CRORE);
          if (finalVal !== value) {
            setLocalValue(finalVal);
          }
        }
      }, [value]);

      const rightSectionSelect = loading ? (
        <Loader size={size} />
      ) : error ? (
        <RiErrorWarningLine />
      ) : (
        <RiArrowDownSLine />
      );

      const inputClasses = useMemo(() => {
        return GroupInputOverrideClassNames({
          size,
          focussed: inputFocussed,
          error
        });
      }, [size, inputFocussed, error]);

      const selectClasses = useMemo(() => {
        return GroupSelectOverrideClassNames({
          size,
          focussed: selectFocussed,
          error
        });
      }, [size, selectFocussed, error]);

      /**
       * when user changes the unit update the current value in state perform calculations
       * to keep values in sync
       * @param currUnit
       */
      const handleUnitClick = (currUnit: string) => {
        if (currUnit === localUnit) return;
        const finalValue = localValue ?? 0;
        setLocalValue(finalValue);
        setLocalUnit(currUnit);
        onChange &&
          onChange(finalValue * (currUnit === 'lakhs' ? ONE_LAKH : ONE_CRORE));
      };

      /**
       * @description handles the changes in input
       * @param value number
       * @param currUnit 'l' | 'cr'
       */
      const handleOnChange = (value: number | undefined, currUnit = localUnit) => {
        if (value || value === 0) {
          setLocalValue(value);
          onChange &&
            onChange(value * (currUnit === 'lakhs' ? ONE_LAKH : ONE_CRORE));
        }
        if (typeof value === 'undefined') {
          setLocalValue(0);
          onChange && onChange(0);
        }
      };

      /**
       * Update the localValue whenever NumberInput onchange event is triggered
       * @param value
       */
      const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
        if (!event.target.value) {
          /**
           * if min was set, set localVal and Unit based on that
           * Otherwise trigger onChange with undefined
           */
          {
            setLocalValue(0);
            onChange && onChange(0);
          }
        } else {
          const targetVal = parseFloat(
            event.target.value.replace(/\$\s?|(,*)/g, '')
          );
          const finalVal = targetVal;
          setLocalValue(finalVal);
          onChange &&
            onChange(finalVal * (localUnit === 'lakhs' ? ONE_LAKH : ONE_CRORE));
        }
      };

      return (
        <Box {...rest} lh={1.55}>
          <Grid gutter={0}>
            {(label || description) && (
              <Grid.Col span={12} mb={4}>
                {label && (
                  <Input.Label
                    fw={500}
                    styles={(theme) => ({
                      label: {
                        color:
                          scheme.colorScheme === 'light'
                            ? theme.colors.text[6]
                            : undefined
                      }
                    })}
                    size={size}
                  >
                    {label}
                  </Input.Label>
                )}
                {description && (
                  <Input.Description fz={10}>{description}</Input.Description>
                )}
              </Grid.Col>
            )}
            <Grid.Col span={5} my={2}>
              <NumberInput
                onFocus={() => setInputFocussed(true)}
                hideControls
                placeholder={numberPlaceholder}
                leftSection={numberIcon}
                rightSection={undefined}
                size={size}
                error={error ? true : false}
                disabled={disabled}
                classNames={{ ...inputClasses }}
                value={localValue}
                clampBehavior="none"
                onBlur={handleBlur}
                onChange={(val) => handleOnChange(val as number)}
                ref={ref}
                decimalScale={decimalScale}
                useCurrencyFormatter={useCurrencyFormatter}
              />
            </Grid.Col>
            <Grid.Col span={7} my={2}>
              <Select
                onFocus={() => setSelectFocussed(true)}
                onBlur={() => setSelectFocussed(false)}
                placeholder={selectPlaceholder}
                leftSection={selectIcon}
                data={[
                  { value: 'lakhs', label: 'Lakhs' },
                  { value: 'crores', label: 'Crores' }
                ]}
                size={size}
                rightSection={rightSectionSelect}
                error={error ? true : false}
                disabled={disabled}
                classNames={{ ...selectClasses }}
                value={localUnit}
                onChange={(e) => e !== null && handleUnitClick(e)}
              />
            </Grid.Col>
            {error && (
              <Grid.Col span={12} mt={4}>
                <Input.Error c="pink.6" size={size}>
                  {error}
                </Input.Error>
              </Grid.Col>
            )}
          </Grid>
        </Box>
      );
    }
  )
);

export { CoverSelectGroup };
export type { CoverSelectGroupProps };
